import React, { useState } from 'react';
import * as S from './styles';
import { FiExternalLink } from 'react-icons/fi';

// Parcela pendente mais antiga: parcelas pagas = total pago / valor da parcela
function proximaParcela(evento, totalPago) {
  const total = Number(evento.numeroParcelas);
  const valor = Number(String(evento.valorParcela).replace(',', '.'));
  const dia = Number(evento.diaVencimento);
  if (!evento.primeiraParcela || !(total > 0) || !(valor > 0)) return null;

  const pagas = Math.floor((totalPago + 0.005) / valor);
  if (pagas >= total) return null;

  const [ano, mes, diaPrimeira] = evento.primeiraParcela.split('-').map(Number);
  const mesAlvo = mes - 1 + pagas;
  const ultimoDia = new Date(ano, mesAlvo + 1, 0).getDate();
  const diaAlvo = pagas === 0 || !dia ? diaPrimeira : Math.min(dia, ultimoDia);
  const vencimento = new Date(ano, mesAlvo, diaAlvo);

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  return { numero: pagas + 1, total, valor, vencimento, atrasada: vencimento < hoje };
}

export default function PagEventoModal({
  aberto,
  onFechar,
  evento = {},
  nomeUserCadastro = '',
  pagamentos = [],
  temArquivosNovos = false,
  anexos,
  aoAlterarEvento,
  aoSelecionarArquivo,
  aoSalvarPagamento,
  aoPagamentoConfirmado,
}) {
  const [enviando, setEnviando] = useState(false);
  const [descricaoPagamento, setDescricaoPagamento] = useState('');
  const [erroEnvio, setErroEnvio] = useState('');

  if (!aberto) return null;

  const pagamentosAtuais = Array.isArray(pagamentos) ? pagamentos : [];
  const pagamentosExibidos = pagamentosAtuais.length > 0 ? pagamentosAtuais : (
    evento.valorPag && evento.dataPag ? [{
      data: evento.dataPag,
      valor: evento.valorPag,
      tipo: evento.formaPag,
    }] : []
  );
  const arquivosPagamento = (Array.isArray(evento.arquivos) ? evento.arquivos : [])
    .filter((arquivo) => /pagamento/i.test(arquivo));
  const orcamento = Number(evento.orcamento || 0);
  const valorParcelaFinanciamento = Number(String(evento.valorParcela).replace(',', '.'));
  const valoresPagamentos = pagamentosExibidos.map((pagamento) =>
    Math.max(Number(pagamento?.valor ?? pagamento?.valorPG ?? 0), 0)
  );
  const principalPago = valoresPagamentos.reduce(
    (total, valor) => total + (evento.tipoEvento === 'Financiamento' && valorParcelaFinanciamento > 0
      ? Math.min(valor, valorParcelaFinanciamento)
      : valor),
    0
  );
  const jurosPagos = evento.tipoEvento === 'Financiamento' && valorParcelaFinanciamento > 0
    ? valoresPagamentos.reduce(
      (total, valor) => total + Math.max(valor - valorParcelaFinanciamento, 0),
      0
    )
    : 0;
  const saldo = Math.max(orcamento - principalPago, 0);
  const valorPagamento = Number(evento.valorPagamento);
  const temArquivosAnexados = temArquivosNovos || arquivosPagamento.length > 0;

  const isFinanciamento = evento.tipoEvento === 'Financiamento';
  const valorPrincipalPagamento = isFinanciamento && valorParcelaFinanciamento > 0
    ? Math.min(valorPagamento, valorParcelaFinanciamento)
    : valorPagamento;
  const parcelaFinanciamento = isFinanciamento ? proximaParcela(evento, principalPago) : null;
  const podeConfirmar = temArquivosAnexados && !!evento.dataPag && !!evento.formaPag
    && Number.isFinite(valorPagamento) && valorPagamento > 0 && valorPrincipalPagamento <= saldo;

  const abrirComprovante = (arquivo) => {
    if (!arquivo) return;
    const url = /^https?:\/\//i.test(arquivo)
      ? arquivo
      : `https://rbento-web.s3.us-east-1.amazonaws.com/${arquivo}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const confirmar = async () => {
    setEnviando(true);
    setErroEnvio('');

    try {
      const salvo = await aoSalvarPagamento(valorPagamento, descricaoPagamento);
      if (!salvo) {
        setErroEnvio('Não foi possível confirmar o pagamento. Confira os dados e tente novamente.');
        return;
      }

      setDescricaoPagamento('');
      onFechar();
      if (aoPagamentoConfirmado) await aoPagamentoConfirmado();
    } catch (error) {
      const erroServidor = error?.response?.data?.error;
      const mensagemErro = typeof erroServidor === 'string'
        ? erroServidor
        : erroServidor?.msg || error?.response?.data?.message || error?.message;
      setErroEnvio(mensagemErro || 'Não foi possível confirmar o pagamento. Confira os dados e tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <S.PagamentoOverlay onClick={onFechar}>
      <S.PagamentoCard onClick={(e) => e.stopPropagation()}>
        <S.PagamentoHeader>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span>Confirmar Pagamento - {evento.placa}</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 400 }}>
                {nomeUserCadastro || ''}
              </span>
          </div>
          <button type="button" onClick={onFechar} aria-label="Fechar">×</button>
        </S.PagamentoHeader>

        <S.PagamentoBody>
          <div className="lista">
            <span className="titulo">Histórico de pagamentos:</span>
            {pagamentosExibidos.length > 0 ? pagamentosExibidos.map((pagamento, indice) => {
              const dataPagamento = pagamento?.data || pagamento?.dataPG;
              const arquivo = pagamento?.arquivo || pagamento?.comprovante || pagamento?.arquivos?.[0] || arquivosPagamento[indice];

              return (
                <div key={`${dataPagamento || 'pagamento'}-${indice}`} style={{ display: 'flex', color: '#000', alignItems: 'center', gap: '8px', fontSize: '0.82rem' }}>
                  <span style={{ flex: 1 }}>
                    {dataPagamento ? new Date(dataPagamento).toLocaleDateString('pt-BR') : 'Sem data'}
                  </span>
                  <span style={{ flex: 1 }}>{pagamento?.tipo || pagamento?.formaPag || 'Pagamento'}</span>
                  <strong>{Number(pagamento?.valor ?? pagamento?.valorPG ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                  {arquivo && (
                    <button
                      type="button"
                      title="Abrir comprovante"
                      aria-label="Abrir comprovante"
                      onClick={() => abrirComprovante(arquivo)}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px', border: 0, background: 'transparent', color: '#2d6cdf', cursor: 'pointer' }}
                    >
                      <FiExternalLink size={16} />
                    </button>
                  )}
                </div>
              );
            }) : <span style={{ color: '#6b7280', fontSize: '0.82rem' }}>Nenhum pagamento registrado.</span>}
          </div>

          <div className="lista">
            <span className="titulo">Orçamento:</span>
            <span style={{ color: '#1e3b9b'}}>{orcamento.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
            <span style={{ color: saldo > 0 ? '#b45309' : '#15803d', fontSize: '0.85rem' }}>
              Pendente: {saldo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </span>
            {isFinanciamento && (
              <span style={{ color: '#ff0000', fontSize: '0.85rem' }}>
                Juros: {jurosPagos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            )}
          </div>

          {isFinanciamento && (
            <div className="lista">
              <span className="titulo">Parcela do financiamento:</span>
              {parcelaFinanciamento ? (
                <>
                  <span style={{color:"black"}}>
                    Parcela {parcelaFinanciamento.numero}/{parcelaFinanciamento.total} - Vencimento: {parcelaFinanciamento.vencimento.toLocaleDateString('pt-BR')}
                  </span>
                  <span style={{ color: parcelaFinanciamento.atrasada ? '#ef4444' : '#15803d', fontSize: '0.85rem' }}>
                    {parcelaFinanciamento.atrasada ? 'Vencida' : 'A vencer'} - Valor: {parcelaFinanciamento.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </span>
                </>
              ) : (
                <span style={{ color: '#15803d', fontSize: '0.85rem' }}>Todas as parcelas foram pagas.</span>
              )}
            </div>
          )}

          <div className="linhaCampos">
            <div className="lista">
              <label className="titulo" htmlFor="data-pagamento">Data Pagamento</label>
              <input
                id="data-pagamento"
                type="date"
                value={evento.dataPag || ''}
                onChange={(e) => aoAlterarEvento('dataPag', e.target.value)}
              />
            </div>

            <div className="lista">
              <label className="titulo" htmlFor="valor-parcela">Valor desta parcela</label>
              <input
                id="valor-parcela"
                type="number"
                min="0.01"
                max={isFinanciamento ? undefined : saldo}
                step="0.01"
                placeholder="0,00"
                value={evento.valorPagamento || ''}
                onChange={(e) => aoAlterarEvento('valorPagamento', e.target.value)}
              />
            </div>

            <div className="lista">
              <label className="titulo" htmlFor="forma-pagamento">Forma Pagamento</label>
              <select
                id="forma-pagamento"
                value={evento.formaPag || ''}
                onChange={(e) => aoAlterarEvento('formaPag', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Pix">Pix</option>
                <option value="Boleto">Boleto</option>
              </select>
            </div>
          </div>

          <div className="lista">
            <label className="titulo" htmlFor="descricao-pagamento">Descrição do pagamento</label>
            <input
              id="descricao-pagamento"
              type="text"
              placeholder="Adicione uma descrição (opcional)"
              value={descricaoPagamento}
              onChange={(e) => setDescricaoPagamento(e.target.value)}
            />
          </div>

          {anexos}
          {aoSelecionarArquivo && 
          <button type="button" onClick={aoSelecionarArquivo}>
          Anexar Arquivo
          </button>}

          {erroEnvio && <span role="alert" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px' }}>{erroEnvio}</span>}
          {!podeConfirmar && (
            <span style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px' }}>
              Preencha os dados, informe uma parcela de até {saldo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} e anexe o comprovante para confirmar.
            </span>
          )}
        </S.PagamentoBody>

        <S.PagamentoFooter>
          <button type="button" onClick={onFechar} disabled={enviando}>Cancelar</button>
          <button type="button" disabled={!podeConfirmar || enviando} onClick={confirmar}>
            {enviando ? 'Processando...' : 'Confirmar Pagamento'}
          </button>
        </S.PagamentoFooter>
      </S.PagamentoCard>
    </S.PagamentoOverlay>
  );
}